import { Auth } from '@/context/UserContext';
import { MenuOption } from '@/types/menu-options';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartSimple,
  faSquarePollVertical,
  faSquarePollHorizontal,
  faChartPie,
  faChartLine,
  faChartColumn,
  faChartBar,
  faPiggyBank,
  faMoneyCheckDollar,
  faMoneyBillTrendUp,
  faMoneyBillTransfer,
  faRankingStar,
  faCreditCard,
  faCoins,
  faSackDollar,
  faHandHoldingDollar,
  faUser,
  faFileSignature,
  faAddressCard,
  faToolbox,
  faPeopleGroup,
  faFileExcel,
  faTable,
  IconDefinition,
  faBriefcase,
  faUsers,
  faCloud,
  faHome,
  faTasks,
  faComments,
  faQuestion,
  faStore,
} from '@fortawesome/free-solid-svg-icons';
import { CustomTabItem, JWTUser, Role } from '@types';

export const iconList: IconDefinition[] = [
  faChartSimple,
  faSquarePollVertical,
  faSquarePollHorizontal,
  faChartPie,
  faChartLine,
  faChartColumn,
  faChartBar,
  faPiggyBank,
  faMoneyCheckDollar,
  faMoneyBillTrendUp,
  faMoneyBillTransfer,
  faRankingStar,
  faCreditCard,
  faCoins,
  faSackDollar,
  faHandHoldingDollar,
  faUser,
  faFileSignature,
  faAddressCard,
  faToolbox,
  faPeopleGroup,
  faFileExcel,
  faTable,
];

const clientOptions = [
  { icon: faHome, label: 'Home', url: '/home' },
  { icon: faUser, label: 'Account', url: '/account' },
  { icon: faCloud, label: 'Files', url: '/files' },
  { icon: faChartLine, label: 'Investor Reporting', url: '/i-reporting' },
  { icon: faQuestion, label: 'FAQ', url: '/faq' },
  { icon: faStore, label: 'Marketplace', url: '/marketplace' },
];

const investorOptions = [
  { icon: faHome, label: 'Home', url: '/i-home' },
  { icon: faCloud, label: 'All Files', url: '/i-files' },
  { icon: faUser, label: 'Account', url: '/i-account' },
  { icon: faChartLine, label: 'Report Builder', url: '/i-reporting' },
];

export const getIconDefinitionByName = (iconName: string) =>
  iconList.find(
    (icon) => icon.iconName.toLowerCase() === iconName.toLowerCase()
  );

export const MENU_BY_USER = {
  superadmin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faUsers, label: 'Users', url: '/users' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
  ],
  specialadmin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
  ],
  admin: [
    { icon: faBriefcase, label: 'Clients', url: '/clients' },
    { icon: faCloud, label: 'OneDrive', url: '/files' },
  ],
  client: clientOptions,
  clientInvestor: [
    { icon: faHome, label: 'Home', url: '/home' },
    { icon: faCloud, label: 'Files', url: '/files' },
  ],
  investor: investorOptions,
};

export const BOTTOM_MENU_BY_USER = {
  superadmin: [] as MenuOption[],
  specialadmin: [] as MenuOption[],
  admin: [] as MenuOption[],
  client: [] as MenuOption[],
  // clientInvestor: isPreProductionEnv
  //   ? [{ icon: faComments, label: 'Chatbot', url: '/chatbot' }]
  //   : [],
  investor: [] as MenuOption[],
};

export const MENU_ADMIN_EXCLUSIVES = [
  {
    icon: faTasks,
    label: 'Categorizations',
    url: '/categorizations',
  },
];

export const isChatbotAllowed = (
  auth: Auth | null | undefined,
  authCompanyChatbotEnabled: boolean | null,
  clientId: number | null,
  companyName: string
) => {
  if ((companyName || '').toLocaleLowerCase().includes('opstart')) {
    return true;
  }
  if (auth?.isInvestor) {
    return false;
  }
  if (auth?.isSuperAdmin) {
    return authCompanyChatbotEnabled || false;
  }
  if (clientId) {
    return (
      (
        auth?.clients.find((e) => e.id === clientId) as unknown as {
          chatbot_enabled: boolean;
        }
      )?.chatbot_enabled || false
    );
  }
  return false;
};

export const getMenuOptions = (
  menuType: Role,
  auth: Auth | null | undefined,
  customTabs: MenuOption[],
  impersonatingAccountId: number | null | undefined,
  companyChatbotEnabled: boolean | null,
  showTransactionsTab: boolean,
  companyName: string
) => {
  let options = [
    ...MENU_BY_USER[menuType],
    ...(auth?.isInvestor ? [] : customTabs),
  ];

  // Conditionally add the Transactions tab
  if (
    ((auth?.isAdmin && impersonatingAccountId) || auth?.isClient) &&
    !!showTransactionsTab
  ) {
    options = [...options, ...MENU_ADMIN_EXCLUSIVES];
  }
  // Conditionally add the Chatbot
  if ((auth?.isAdmin && impersonatingAccountId) || auth?.isClient) {
    options = [
      ...options,
      ...[{ icon: faComments, label: 'Chatbot', url: '/chatbot' }],
    ];
  }

  if (auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/chatbot');
  }

  if (
    (auth?.isAdmin &&
      impersonatingAccountId &&
      !isChatbotAllowed(
        auth,
        companyChatbotEnabled,
        impersonatingAccountId,
        companyName || ''
      )) ||
    (auth?.isClient &&
      auth?.clients?.[0]?.id &&
      !isChatbotAllowed(
        auth,
        companyChatbotEnabled,
        auth?.clients?.[0]?.id,
        auth?.clients?.[0]?.client_name
      ))
  ) {
    options = options.filter((option) => option.url !== '/chatbot');
  }

  // Remove the /account option for investors when impersonating
  if (impersonatingAccountId && auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/account');
  }

  if (auth?.role === 'investor' && auth?.firms?.length === 0) {
    options = options.filter((option) => option.url !== '/i-account');
  }
  return options as MenuOption[];
};

export const getBottomMenuOptions = (
  menuType: Role,
  auth: Auth | null | undefined,
  impersonatingAccountId: number | null | undefined
) => {
  let options = [...BOTTOM_MENU_BY_USER[menuType]];

  // Remove the /account option for investors when impersonating
  if (impersonatingAccountId && auth?.role === 'investor') {
    options = options.filter((option) => option.url !== '/account');
  }

  if (auth?.role === 'investor' && auth?.firms?.length === 0) {
    options = options.filter((option) => option.url !== '/i-account');
  }

  return options;
};

export const prepareCustomTabs = (
  customTabInfo: CustomTabItem[] | null
): MenuOption[] => {
  return (customTabInfo || []).map((customTab: CustomTabItem) => ({
    icon: getIconDefinitionByName(customTab.icon) as IconProp,
    label: customTab.titleOfTab,
    url: `/custom-tab-option/${encodeURIComponent(
      customTab.titleOfTab
    )}?embeddedLink=${encodeURIComponent(
      customTab.embeddedLink
    )}&documentLink=${encodeURIComponent(customTab.documentLink)}`,
  }));
};

export const menuType = (
  impersonatingAccountId: number | null,
  auth: Auth | null | undefined
) => {
  if (impersonatingAccountId && auth?.isAdmin) {
    return 'client';
  }
  return (auth as JWTUser)?.role || 'client';
};
